/**
 * Values that both the API and the UI have to agree on. Anything here is
 * duplicated nowhere else: the server validates against it and the client
 * renders from it, so a new WoW class or theme is a one-line change.
 */
export const LOCALES = ['fr', 'en'];
export const DEFAULT_LOCALE = 'fr';
/**
 * `custom` is not a palette but a marker: the user's own colour values live in
 * `users.theme_custom` and are applied as inline CSS variables.
 */
export const THEMES = ['light', 'dark', 'alliance', 'horde', 'void', 'poison', 'custom'];
export const DEFAULT_THEME = 'dark';
/** The CSS custom properties a `custom` theme may override. */
export const CUSTOM_THEME_KEYS = ['bg', 'surface', 'text', 'accent', 'accentContrast'];
export const USER_ROLES = ['member', 'admin'];
export const FACTIONS = ['alliance', 'horde', 'neutral'];
/** Class ids kept lowercase and hyphenated so they double as CSS class names. */
export const WOW_CLASSES = [
    'death-knight',
    'demon-hunter',
    'druid',
    'evoker',
    'hunter',
    'mage',
    'monk',
    'paladin',
    'priest',
    'rogue',
    'shaman',
    'warlock',
    'warrior',
];
/** Blizzard's official class colours — the roster is unreadable without them. */
export const CLASS_COLORS = {
    'death-knight': '#C41E3A',
    'demon-hunter': '#A330C9',
    druid: '#FF7C0A',
    evoker: '#33937F',
    hunter: '#AAD372',
    mage: '#3FC7EB',
    monk: '#00FF98',
    paladin: '#F48CBA',
    priest: '#F0F0F0',
    rogue: '#FFF468',
    shaman: '#0070DD',
    warlock: '#8788EE',
    warrior: '#C69B6D',
};
export const WOW_RACES = [
    'human',
    'dwarf',
    'night-elf',
    'gnome',
    'draenei',
    'worgen',
    'void-elf',
    'lightforged-draenei',
    'dark-iron-dwarf',
    'kul-tiran',
    'mechagnome',
    'orc',
    'undead',
    'tauren',
    'troll',
    'blood-elf',
    'goblin',
    'nightborne',
    'highmountain-tauren',
    'maghar-orc',
    'zandalari-troll',
    'vulpera',
    'pandaren',
    'dracthyr',
    'earthen',
];
export const ROLES = ['tank', 'healer', 'dps'];
export const MAX_CHARACTER_LEVEL = 80;
/** How a character earns its place in the roster view. */
export const CHARACTER_KINDS = ['main', 'reroll', 'alt', 'bank'];
export const LOG_SOURCES = ['warcraftlogs', 'raider-io', 'wowanalyzer', 'other'];
export const MEDIA_KINDS = ['image', 'video', 'audio', 'link'];
export const NOTIFICATION_TYPES = [
    'post.created',
    'comment.created',
    'reaction.created',
    'storage.warning',
    'system',
];
/**
 * Uploads are meant to stay "light": a meme, a short clip, a voice line. These
 * ceilings are enforced per file by multer AND re-checked against the pool
 * quota, so the library can never grow past what the admin allowed.
 */
export const MAX_UPLOAD_BYTES = {
    image: 12 * 1024 * 1024, // 12 MiB - plenty for a GIF
    video: 40 * 1024 * 1024, // 40 MiB - a few seconds of clip
    audio: 12 * 1024 * 1024, // 12 MiB - a soundboard line
};
/** The largest single upload of any kind; multer's hard `fileSize` limit. */
export const MAX_UPLOAD_BYTES_ANY = Math.max(...Object.values(MAX_UPLOAD_BYTES));
export const ALLOWED_MIME_TYPES = {
    image: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif'],
    video: ['video/mp4', 'video/webm'],
    audio: ['audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/webm', 'audio/mp4'],
};
export const ALL_ALLOWED_MIME_TYPES = Object.values(ALLOWED_MIME_TYPES).flat();
/**
 * A character's "hero" media — a GIF or a short mp4 clip that replaces its
 * static Armory render once focused — reuses the forum's own upload
 * endpoint and storage quota, but with its own, tighter size ceiling.
 */
export const MAX_CHARACTER_MEDIA_BYTES = 20 * 1024 * 1024;
export const CHARACTER_MEDIA_MIME_TYPES = ['video/mp4', 'image/gif'];
/** Default pool: 5 GiB, as asked. The admin can change it from the UI. */
export const DEFAULT_STORAGE_LIMIT_BYTES = 5 * 1024 * 1024 * 1024;
/**
 * An upload is refused once the pool would pass this share of the limit, so we
 * stop just short of full rather than wedging the library at exactly 100 %.
 */
export const STORAGE_REFUSAL_RATIO = 0.98;
/** Above this share the UI nags and admins get a notification. */
export const STORAGE_WARNING_RATIO = 0.85;
export const SETTING_KEYS = [
    'storage_limit_bytes',
    'site_title',
    'public_roster',
    'wow_region',
    'warcraftlogs_zone_id',
    'default_theme',
    'default_theme_custom',
];
/** Blizzard/Raider.IO/Warcraft Logs all key data off the same four regions. */
export const WOW_REGIONS = ['eu', 'us', 'kr', 'tw'];
export const DEFAULT_WOW_REGION = 'eu';
/** Warcraft Logs difficulty ids, used verbatim in its GraphQL API. */
export const WCL_DIFFICULTY_IDS = { normal: 3, heroic: 4, mythic: 5 };
/**
 * A full-roster resync is expensive (three external API calls per
 * character) and open to anyone, logged in or not — this single global
 * cooldown (not a per-user one) is the only thing standing between it and
 * abuse, so both sides need to agree on exactly how long it is.
 */
export const SYNC_ALL_COOLDOWN_MS = 5 * 60 * 1000;
export const PASSWORD_MIN_LENGTH = 10;
export const USERNAME_PATTERN = /^[a-zA-Z0-9._-]{3,32}$/;
export const PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;
//# sourceMappingURL=constants.js.map